import { useEffect, useState, useRef } from 'react';
import { Form, useFormikContext } from 'formik';
import { BrandedEmailTemplateParams, EmailType } from '@packlink/packlink-sdk';
import { useToggle } from '@packlink/utils';
import { Tab, TabList, Typography } from '@shipengine/giger';
import { useTranslation } from '@packlink/translation-provider';
import { getViewAmplitudePayload, useAmplitude, useAvailableLanguages } from '@common/hooks';
import { EditTemplateHeader } from './components/Header/EditTemplateHeader';
import { emailTypesKeys } from './components/EmailTemplatesList/constants';
import { getLanguageHeaderStyles, getTabStyles } from './BrandedEmailEditionContentStyles';
import { EmailTemplateEdition } from './components/EmailTemplateEdition/EmailTemplateEdition';
import { LanguageTabOptions } from './BrandedEmailEdition';
import { UnsavedChangesDialog } from './components/UnsavedChangesDialog/UnsavedChangesDialog';
import { AmplitudeEventsBulkBrandedEmails } from './metrics/amplitude';
import { useBrandedEmailsSettings } from './hooks/useBrandedEmailsSettings';

export type BrandedEmailContentProps = {
    emailType: EmailType;
    selectedLanguage?: LanguageTabOptions;
    setSelectedLanguage: (language?: LanguageTabOptions) => void;
};

export function BrandedEmailEditionContent({
    emailType,
    selectedLanguage,
    setSelectedLanguage,
}: BrandedEmailContentProps): JSX.Element {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();
    const [options, setOptions] = useState<LanguageTabOptions[]>([]);

    const { state: isUnsavedDialogOpen, toggle: toggleUnsavedDialog } = useToggle();
    const { sendAmplitudeClickEvent } = useAmplitude();
    const { availableLanguages } = useAvailableLanguages();
    const { brandedEmails } = useBrandedEmailsSettings(true);

    const { isSubmitting, touched, handleSubmit, errors } = useFormikContext<BrandedEmailTemplateParams>();
    const isFormTouched = Object.keys(touched).length > 0;

    const pendingLanguageOptionRef = useRef<LanguageTabOptions | null>(null);

    useEffect(() => {
        setOptions(
            availableLanguages
                .map(
                    (lang): LanguageTabOptions => ({
                        label: lang.language,
                        value: lang.locale,
                    }),
                )
                .sort((a, b) => {
                    // Ensure the current locale is always first in the list
                    if (a.value === locale) return -1;
                    if (b.value === locale) return 1;
                    return 0;
                }),
        );
    }, [availableLanguages, locale]);

    useEffect(() => {
        setSelectedLanguage(options.find((option: LanguageTabOptions): boolean => option.value === locale));
    }, [locale, options, setSelectedLanguage]);

    useEffect(() => {
        if (!isSubmitting && pendingLanguageOptionRef.current) {
            setSelectedLanguage(pendingLanguageOptionRef.current);
            pendingLanguageOptionRef.current = null;
        }
    }, [isSubmitting, setSelectedLanguage]);

    const handleSaveOnDialog = () => {
        sendAmplitudeClickEvent(
            AmplitudeEventsBulkBrandedEmails.BRANDED_EMAILS_SAVE_BRANDED_EMAIL_MODAL,
            getViewAmplitudePayload(locale),
        );
        handleSubmit();
        toggleUnsavedDialog();
    };

    const isTabDisabled = Object.keys(errors).length > 0 || isSubmitting;

    const isEmailActive =
        brandedEmails?.emails?.some((email) => email.emailType === emailType && email.active) ?? false;

    return (
        <Form>
            <EditTemplateHeader template={{ emailType, active: isEmailActive }} isFormTouched={isFormTouched} />
            <div css={getLanguageHeaderStyles}>
                <Typography component="h3" variant="heading3">
                    {t(`branded-emails.edition.page-title-${emailTypesKeys[emailType]}`)}
                </Typography>
                <TabList>
                    {options.map((option: LanguageTabOptions) => (
                        <Tab
                            css={getTabStyles(isTabDisabled)}
                            key={option.value}
                            selected={selectedLanguage?.value === option.value}
                            onClick={(_selectedItem, e) => {
                                e?.preventDefault();
                                if (isTabDisabled) return;
                                if (isFormTouched) {
                                    pendingLanguageOptionRef.current = option;
                                    toggleUnsavedDialog();
                                } else {
                                    setSelectedLanguage({ label: option.label, value: option.value });
                                }
                            }}
                            data-id={`branded-emails.edition.tab-${option.value}`}
                        >
                            {option.label}
                        </Tab>
                    ))}
                </TabList>
            </div>
            <EmailTemplateEdition
                companyLogoUrl={brandedEmails?.brandedSettings?.companyLogoUrl}
                emailType={emailType}
                isFormTouched={isFormTouched}
                deliveryAddress="Av. Ciudad de Barcelona, 20 Madrid 28001"
                deliveryDateNumber={28}
                selectedLanguage={selectedLanguage}
            />
            <UnsavedChangesDialog
                isOpen={isUnsavedDialogOpen}
                onClose={toggleUnsavedDialog}
                onSave={handleSaveOnDialog}
                onCancel={toggleUnsavedDialog}
                isSubmitting={isSubmitting}
            />
        </Form>
    );
}
